/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2016, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 * @module dashboard/filters/pagecontext/PageContextEntry
 * @see PageContextEntry
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'underscore'
], function(Class, _) {

	/**
	 * The pageContextResult is provided in pagecontext:filterContextUpdated events.
	 * It is intended to standardize the form of a pageContext event as a set of api's and
	 * simplify processing the details of a page context update for external callers
	 * (such as the filter dock, memberfiltercontroller, cardWidget).
	 */
	var PageContextResult = Class.extend({


		init: function(attrs /*, options*/ ) {
			attrs = attrs || {};
			this._items = (attrs.response && attrs.response.items) || null;
			this._updated = (attrs.response && attrs.response.updated) || false;
			this._added = (attrs.response && attrs.response.added) || false;
			this._deleted = (attrs.response && attrs.response.deleted) || false;
			this._cleared = (attrs.response && attrs.response.cleared) || false;
			this._isGeneralUpdate = (attrs.source && attrs.source.multi) || false;
			this._noSelfRefresh = (attrs.response && attrs.response.noSelfRefresh) || false;
			this._ignorePageContextChanged = (attrs.response && attrs.response.ignorePageContextChanged) || false;
			this._itemExpressionID = attrs.source && attrs.source.id || false;
			if (attrs.brushingOptions) {
				this._sender = attrs.brushingOptions.sourceId; //There is always a sourceId for brushing.
				this._brushingOptions = attrs.brushingOptions;
			} else {
				this._sender = (attrs.source && attrs.source.sourceId) || null;
			}

			if (attrs.source && attrs.source.eventSourceId) {
				this._sender = attrs.source.eventSourceId;
			}

			this.dataSources = attrs.dataSources;

			this.api = {
				containsItem: this.containsItem.bind(this),
				getSourceIds: this.getSourceIds.bind(this),
				getAssetIds: this.getAssetIds.bind(this),
				getItems: this.getItems.bind(this),
				getItemExpressionID: this.getItemExpressionID.bind(this),
				isAdd: this.isAdd.bind(this),
				isDelete: this.isDelete.bind(this),
				isClear: this.isClear.bind(this),
				isGeneralUpdate: this.isGeneralUpdate.bind(this),
				isUpdate: this.isUpdate.bind(this),
				isInScope: this.isInScope.bind(this),
				senderMatches: this.senderMatches.bind(this),
				brushingChanged: this.brushingChanged.bind(this),
				noSelfRefresh: this.noSelfRefresh.bind(this),
				ignorePageContextChanged: this.ignorePageContextChanged.bind(this)
			};
		},

		/**
		 * The api for this object is returned in the event payload
		 * to only allow clients to see the api methods.
		 */
		getAPI: function() {
			return this.api;
		},

		/**
		 * @returns all items affected by this pageContext operation.
		 */
		getItems: function() {
			return this._items;
		},

		/**
		 * @param item - an item to search for in this response.
		 * @return s true if the items affected include the specified item.
		 */
		containsItem: function(item) {
			return _.find(this._items, function(resultItem) {
				return (item === resultItem);
			}) ? true : false;
		},

		/**
		 * @returns true if this page context result includes changes to the brushing state (which means at least 1 modified entry is of origin 'visualization')
		 */
		brushingChanged: function() {
			if (this._brushingOptions && this._items && !this._items.length) {
				//idviz sends us an event with an empty items array when brushing is cleared.
				return true;
			}
			return _.find(this._items, function(resultItem) {
				return (resultItem.getOrigin() === 'visualization');
			}) ? true : false;
		},

		/**
		 * @returns true if the response is deemed a 'general update' where items should not be processed individually
		 *			(TODO: This is currently used in undo/redo, deleteAll and clearAll but may be deprecated in future)
		 */
		isGeneralUpdate: function() {
			return this._isGeneralUpdate;
		},

		isAdd: function() {
			return this._added;
		},

		isUpdate: function() {
			return this._updated;
		},

		isDelete: function() {
			return this._deleted;
		},

		isClear: function() {
			return this._cleared;
		},

		/**
		 * @returns the source of this event if known (such as a widgetId) or null.
		 */
		getSender: function() {
			return this._sender;
		},

		/**
		 * @returns the string built from item expression and is used as a key in filter dock.
		 */
		getItemExpressionID: function() {
			return this._itemExpressionID;
		},

		/**
		 * @param senderId - the id of an event source (usu the filter widget).
		 * @returns true if the senderId matches
		 */
		senderMatches: function(senderId) {
			return (this.getSender() === senderId);
		},

		/**
		 * return either an array or an index (map) with the sourceIds that are affected in this items list.
		 * @param asMap (optional) - if true, the response will be returned in map form for indexed lookup.
		 * @returns a map or an array of sourceIds referenced in the items list.
		 */
		getSourceIds: function( /*asMap*/ asMap) {
			//TODO: The two members under source (sourceId and sourceIdsToUpdate)
			//		will probably be deprecated in favour of using only the item sourceIds.
			var sourceIdsToUpdate = (this.source && this.source.sourceIdsToUpdate);
			if (!sourceIdsToUpdate) {
				sourceIdsToUpdate = {};
				_.each(this._items, function(item) {
					sourceIdsToUpdate[item.getSourceId()] = true;
				});
				if (this.source && this.source.sourceId) {
					sourceIdsToUpdate[this.source.sourceId] = true;
				}
			}
			return (asMap) ? sourceIdsToUpdate : _.keys(sourceIdsToUpdate);
		},

		/**
		 * Returns a an array of assetIds that are valid for this pageContextResult
		 * @return {[String]}
		 */
		getAssetIds: function() {
			var sourceIds = this.getSourceIds();
			var assetIds = [];
			sourceIds.forEach(function(sourceId) {
				var source = this.dataSources.getSource(sourceId);
				if (source) {
					assetIds.push(source.getAssetId());
				}
			});

			return assetIds;
		},

		/**
		 * determine if the items in this response include ones that affect the passed in scope.
		 * NOTE: This function always returns true if any of the items in this response are at global scope.
		 * @param scope - a scope (tabid) to compare against.
		 * @param eventGroupId (optional) - a event group scope (eventGroupId) to compare against.
		 * @returns true if the items affected in this response include items that are at scope 'tabId' or global.
		 */
		isInScope: function(scope, eventGroupId) {
			if (this._brushingOptions && this._brushingOptions.scope === scope && (!eventGroupId || eventGroupId === this._brushingOptions.eventGroupId)) {
				return true;
			}

			var itemInScope = _.find(this._items, function(item) {
				return item.getScope() === 'global' || item.getScope() === scope;
			});

			if (eventGroupId && itemInScope) {
				// if the origin is filter, we don't care about the event group ids. A filter applies to all event groups of a given scope
				return itemInScope.getOrigin() === 'filter' || itemInScope.getEventGroupId() === eventGroupId;
			}
			return itemInScope ? true : false;
		},

		noSelfRefresh: function() {
			return this._noSelfRefresh;
		},

		ignorePageContextChanged: function() {
			return this._ignorePageContextChanged;
		}

	});

	return PageContextResult;
});
